import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useAuditApprovals } from "services/hooks/auditApprovals";
import { useOrganisations } from "services/hooks/rentals";
import { useSelector } from "react-redux";
import {
  INBOX_VIEWS,
  REQUEST_STATUS_COLORS,
  REQUEST_STATUS_LABELS,
  getMakerDisplayName,
} from "../constants";
import ApprovalDetailDialog from "./ApprovalDetailDialog";

const ApprovalInboxTab = ({ initialView = "pending_for_me" }) => {
  const role = useSelector((state) => state.role);
  const isAdmin = role === "admin";
  const {
    requests,
    isLoadingRequests,
    requestsError,
    fetchRequests,
    approveRequest,
    rejectRequest,
    applyApprovedRequest,
    isSubmittingRequest,
    requestActionError,
    refresh,
  } = useAuditApprovals();
  const { organisations } = useOrganisations({ enabled: isAdmin });

  const [view, setView] = useState(initialView);
  const [statusFilter, setStatusFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [approvingId, setApprovingId] = useState(null);

  React.useEffect(() => {
    setView(initialView);
  }, [initialView]);

  const loadRequests = () => {
    const filters = { limit: 50, offset: 0 };
    if (view === "pending_for_me") filters.pending_for_me = true;
    else if (view === "mine") filters.mine = true;
    if (statusFilter) filters.status = statusFilter;
    if (entityFilter) filters.entity_code = entityFilter;
    if (isAdmin && orgFilter) filters.organization_id = orgFilter;
    fetchRequests(filters);
  };

  React.useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, statusFilter, entityFilter, orgFilter]);

  const handleOpenDetail = (row) => {
    setSelectedRequest(row);
    setDetailOpen(true);
  };

  const handleApprove = async (id, comment = "", requestRow = null) => {
    setApprovingId(id);
    const result = await approveRequest(id, comment);
    if (!result.success) {
      setApprovingId(null);
      setMessage({ type: "error", text: result.message });
      return;
    }

    const approved = result.data;
    if (approved?.status === "APPROVED") {
      const applyTarget = {
        ...requestRow,
        ...approved,
      };
      const applyResult = await applyApprovedRequest(applyTarget);
      setApprovingId(null);
      if (applyResult.success) {
        setMessage({ type: "success", text: "Request approved and applied." });
      } else {
        setMessage({
          type: "error",
          text: `Approved, but the action was not applied: ${applyResult.message}`,
        });
      }
    } else if (approved?.status === "APPLIED") {
      setApprovingId(null);
      setMessage({ type: "success", text: "Request approved and applied." });
    } else {
      setApprovingId(null);
      setMessage({ type: "success", text: "Request approved." });
    }
    setDetailOpen(false);
    loadRequests();
  };

  const handleQuickApprove = async (row) => {
    if (row.status !== "PENDING") return;
    await handleApprove(row.id, "", row);
  };

  const handleReject = async (id, comment) => {
    const result = await rejectRequest(id, comment);
    if (result.success) {
      setMessage({ type: "success", text: "Request rejected." });
      setDetailOpen(false);
      loadRequests();
    } else {
      setMessage({ type: "error", text: result.message });
    }
  };

  const handleApply = async (request) => {
    setApprovingId(request.id);
    const result = await applyApprovedRequest(request);
    setApprovingId(null);
    if (result.success) {
      setMessage({ type: "success", text: "Action applied successfully." });
      setDetailOpen(false);
      loadRequests();
    } else {
      setMessage({ type: "error", text: result.message });
    }
  };

  const rows = requests.results ?? [];

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type || "info"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}
      {requestActionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => refresh()}>
          {requestActionError}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>View</InputLabel>
          <Select value={view} label="View" onChange={(e) => setView(e.target.value)}>
            {INBOX_VIEWS.map((v) => (
              <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.keys(REQUEST_STATUS_LABELS).map((s) => (
              <MenuItem key={s} value={s}>{REQUEST_STATUS_LABELS[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Entity code"
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          placeholder="landlord_payout"
        />
        {isAdmin && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Organization</InputLabel>
            <Select value={orgFilter} label="Organization" onChange={(e) => setOrgFilter(e.target.value)}>
              <MenuItem value="">All orgs</MenuItem>
              {(organisations ?? []).map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="outlined" onClick={loadRequests}>Refresh</Button>
      </Box>

      {isLoadingRequests ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : requestsError ? (
        <Alert severity="error">Failed to load approval requests.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Submitted by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No requests found.</TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.summary || row.entity_label || "—"}</TableCell>
                    <TableCell>{row.entity_code}</TableCell>
                    <TableCell>{row.amount ?? "—"}</TableCell>
                    <TableCell>{getMakerDisplayName(row)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={REQUEST_STATUS_LABELS[row.status] ?? row.status}
                        color={REQUEST_STATUS_COLORS[row.status] ?? "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {row.date_created ? moment(row.date_created).format("MMM D, YYYY HH:mm") : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {row.status === "PENDING" && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={isSubmittingRequest && approvingId === row.id}
                            onClick={() => handleQuickApprove(row)}
                          >
                            {isSubmittingRequest && approvingId === row.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              "Approve"
                            )}
                          </Button>
                        )}
                        {row.status === "APPROVED" && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={isSubmittingRequest && approvingId === row.id}
                            onClick={() => handleApply(row)}
                          >
                            {isSubmittingRequest && approvingId === row.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              "Complete action"
                            )}
                          </Button>
                        )}
                        <Button size="small" variant="outlined" onClick={() => handleOpenDetail(row)}>
                          View
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ApprovalDetailDialog
        open={detailOpen}
        request={selectedRequest}
        onClose={() => setDetailOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        onApply={handleApply}
        isSubmitting={isSubmittingRequest}
      />
    </Box>
  );
};

export default ApprovalInboxTab;
